import type { Axios } from "axios";
import { function as function_, taskEither } from "fp-ts";

import { ImageServicePorts } from "../../model";

import { URLS } from "./consts";
import { Create, Delete, GetMany } from "./ios";
import { mapDtoToEntity } from "./mappers";

import { Response } from "~/shared/api";
import { AuthenticationError, NotFoundError, UnexpectedError } from "~/shared/error";

class ApiProvider extends ImageServicePorts.Provider {
  private readonly axios: Axios;

  constructor(axios: Axios) {
    super();
    this.axios = axios;
  }

  override create(
    params: ImageServicePorts.ProviderIos.Create.In,
  ): taskEither.TaskEither<
    UnexpectedError | AuthenticationError,
    ImageServicePorts.ProviderIos.Common.OutSchema
  > {
    const formData = new FormData();
    for (const [key, value] of Object.entries(params)) {
      formData.append(key, value);
    }

    return function_.pipe(
      Response.process(() => this.axios.postForm(URLS.create, formData), Create.zRes),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) return taskEither.right(mapDtoToEntity(response.body.data));

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override delete({
    id,
  }: ImageServicePorts.ProviderIos.Delete.In): taskEither.TaskEither<
    UnexpectedError | AuthenticationError | NotFoundError,
    void
  > {
    return function_.pipe(
      Response.process(() => this.axios.delete(URLS.delete(id)), Delete.zRes),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 204) return taskEither.right(void 0);

        if (status === 401) return taskEither.left(new AuthenticationError());

        if (status === 404) return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override getMany(): taskEither.TaskEither<
    UnexpectedError | AuthenticationError,
    ImageServicePorts.ProviderIos.GetMany.Out
  > {
    return function_.pipe(
      Response.process(() => this.axios.get(URLS.getMany), GetMany.zRes),
      taskEither.flatMap((response) => {
        const { status, body } = response;
        if (status === 200) return taskEither.right(body.data.map(mapDtoToEntity));

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }
}
export { ApiProvider };
